/**
 * Create-project dialog — V3 §B2.
 *
 * Lives in the UI store rather than on `/projects` so the sidebar "+" and the
 * command palette can open it from anywhere.
 *
 * The original dead-locked silently: the submit button was
 * `disabled={!name || key.length < 2 || !leadId}` with nothing on screen
 * explaining which of those was unmet. Worse, `leadId` initialised from
 * `user?._id` — `''` when auth had not resolved yet — so the Lead select
 * *displayed* a name while the state behind it was empty and the button stayed
 * dead no matter what you typed. Now the button is always live and validation
 * reports per field.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { FolderPlus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { PROJECT_TYPES } from '@virtuetasks/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/features/auth';
import { useUsers } from '@/features/settings/api';
import { useUiStore } from '@/stores/ui';
import { useCreateProject } from './api';

const EMOJI_CHOICES = ['📁', '🩺', '🛒', '🏋', '📚', '⚙️', '🚀', '🎨', '📣', '🧪'];

interface FieldErrors {
  name?: string;
  key?: string;
  leadId?: string;
}

/** Derives a key from the project name: initials, else the first letters. */
export function deriveKey(name: string): string {
  const words = name.replace(/[^A-Za-z0-9 ]/g, '').split(/\s+/).filter(Boolean);
  const initials = words.map((w) => w[0]).join('').toUpperCase().slice(0, 5);
  if (initials.length >= 2) return initials;
  return name.replace(/[^A-Za-z0-9]/g, '').slice(0, 3).toUpperCase();
}

export function validate(fields: { name: string; key: string; leadId: string }): FieldErrors {
  const errors: FieldErrors = {};
  if (!fields.name.trim()) errors.name = 'Give the project a name';
  if (fields.key.length < 2) errors.key = 'At least 2 characters';
  else if (fields.key.length > 5) errors.key = 'At most 5 characters';
  if (!fields.leadId) errors.leadId = 'Pick who leads this project';
  return errors;
}

export function CreateProjectDialog() {
  const open = useUiStore((s) => s.createProjectOpen);
  const setOpen = useUiStore((s) => s.setCreateProjectOpen);
  const create = useCreateProject();
  const { user } = useAuth();
  const { data: people = [] } = useUsers();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [key, setKey] = useState('');
  const [icon, setIcon] = useState('📁');
  const [leadId, setLeadId] = useState('');
  const [qaEnabled, setQaEnabled] = useState(false);
  const [guide, setGuide] = useState('');
  // V4 F-1 — the optional About step.
  const [projectType, setProjectType] = useState('');
  const [tech, setTech] = useState('');
  const [keyTouched, setKeyTouched] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);

  // Default the lead to whoever is creating it, once auth has resolved. The
  // original set this in useState, which ran before `user` existed.
  useEffect(() => {
    if (open && user?._id) setLeadId((current) => current || user._id);
  }, [open, user?._id]);

  function reset(): void {
    setName('');
    setKey('');
    setKeyTouched(false);
    setGuide('');
    setProjectType('');
    setTech('');
    setQaEnabled(false);
    setErrors({});
    setFormError(null);
  }

  function onName(value: string): void {
    setName(value);
    setErrors((e) => ({ ...e, name: undefined }));
    if (!keyTouched) setKey(deriveKey(value));
  }

  async function submit(): Promise<void> {
    setFormError(null);
    const found = validate({ name, key, leadId });
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    try {
      const project = await create.mutateAsync({
        name: name.trim(),
        key,
        icon,
        leadId,
        settings: { qaEnabled },
        ...(guide.trim() ? { guidePurpose: guide.trim() } : {}),
        ...(projectType || tech.trim()
          ? {
              about: {
                ...(projectType ? { projectType } : {}),
                techStack: tech
                  .split(',')
                  .map((t) => t.trim())
                  .filter(Boolean),
              },
            }
          : {}),
      });
      toast.success(`Project ${key} created`);
      reset();
      setOpen(false);
      // Straight to Overview when they wrote something there — otherwise the
      // About they just filled in is invisible until they go looking.
      navigate(`/projects/${project.key}/${projectType || tech.trim() ? 'overview' : 'board'}`);
    } catch (err) {
      if (err instanceof ApiError) {
        // The API knows about key collisions; surface it on the field itself.
        const detail = err.details?.[0];
        if (err.code === 'DUPLICATE_KEY' || detail?.field === 'key') {
          setErrors((e) => ({ ...e, key: detail?.message ?? `Key ${key} is already taken` }));
          return;
        }
        setFormError(detail?.message ?? err.message);
        return;
      }
      setFormError('Could not create the project');
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        setOpen(next);
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogTitle className="mb-4 text-[16px] font-semibold">New project</DialogTitle>
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="p-name">Name</Label>
            <Input
              id="p-name"
              autoFocus
              value={name}
              onChange={(e) => onName(e.target.value)}
              aria-invalid={Boolean(errors.name)}
              aria-describedby={errors.name ? 'p-name-error' : undefined}
            />
            {errors.name && (
              <p id="p-name-error" className="text-[11px] text-[var(--danger)]">
                {errors.name}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="p-key">Key</Label>
              <Input
                id="p-key"
                className="vt-mono uppercase"
                maxLength={5}
                value={key}
                onChange={(e) => {
                  setKeyTouched(true);
                  setKey(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''));
                  setErrors((err) => ({ ...err, key: undefined }));
                }}
                aria-invalid={Boolean(errors.key)}
                aria-describedby={errors.key ? 'p-key-error' : 'p-key-hint'}
              />
              {errors.key ? (
                <p id="p-key-error" className="text-[11px] text-[var(--danger)]">
                  {errors.key}
                </p>
              ) : (
                <p id="p-key-hint" className="text-[11px] vt-muted">
                  2–5 characters. Permanent — task keys use it.
                </p>
              )}
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="p-lead">Lead</Label>
              <select
                id="p-lead"
                className="h-8 rounded-[6px] border border-[var(--border)] bg-[var(--surface)] px-2 text-[13px]"
                value={leadId}
                onChange={(e) => {
                  setLeadId(e.target.value);
                  setErrors((err) => ({ ...err, leadId: undefined }));
                }}
                aria-invalid={Boolean(errors.leadId)}
              >
                <option value="">Select a lead…</option>
                {people
                  .filter((p) => p.status === 'active')
                  .map((p) => (
                    <option key={p._id} value={p._id}>
                      {p.name}
                    </option>
                  ))}
              </select>
              {errors.leadId && (
                <p className="text-[11px] text-[var(--danger)]">{errors.leadId}</p>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Icon</Label>
            <div className="flex flex-wrap gap-1">
              {EMOJI_CHOICES.map((e) => (
                <button
                  key={e}
                  type="button"
                  aria-pressed={icon === e}
                  aria-label={`Icon ${e}`}
                  onClick={() => setIcon(e)}
                  className={`size-8 rounded-[6px] border text-[16px] transition-colors duration-[120ms] ${
                    icon === e ? 'border-[var(--accent)] bg-[var(--accent-soft)]' : 'border-[var(--border)]'
                  }`}
                >
                  {e}
                </button>
              ))}
            </div>
          </div>

          {/* R4: seeds the Purpose section of the auto-created Project Guide. */}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="p-guide">
              Project guide <span className="font-normal vt-muted">— optional</span>
            </Label>
            <textarea
              id="p-guide"
              rows={2}
              value={guide}
              onChange={(e) => setGuide(e.target.value)}
              placeholder="What is this project for? Seeds the pinned Project Guide."
              className="rounded-[6px] border border-[var(--border)] bg-[var(--surface)] px-2 py-1.5 text-[13px]"
            />
          </div>

          {/* V4 F-1 — the About step. Optional, and everything here is
              editable later on the Overview screen by a lead or admin. */}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="p-type">
              Type <span className="font-normal vt-muted">— optional</span>
            </Label>
            <select
              id="p-type"
              data-testid="create-project-type"
              value={projectType}
              onChange={(e) => setProjectType(e.target.value)}
              className="h-8 rounded-[6px] border border-[var(--border)] bg-[var(--surface)] px-2 text-[13px]"
            >
              <option value="">Not set</option>
              {PROJECT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t[0].toUpperCase() + t.slice(1)}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="p-tech">
              Tech stack <span className="font-normal vt-muted">— optional, comma separated</span>
            </Label>
            <Input
              id="p-tech"
              data-testid="create-project-tech"
              value={tech}
              onChange={(e) => setTech(e.target.value)}
              placeholder="flutter, node, mongodb"
            />
          </div>

          <label className="flex items-center gap-2 text-[13px]">
            <input type="checkbox" checked={qaEnabled} onChange={(e) => setQaEnabled(e.target.checked)} />
            Enable QA — adds an “In QA” column between In Review and Blocked
          </label>

          {formError && (
            <p
              role="alert"
              className="rounded-[6px] bg-[var(--danger)]/10 px-2.5 py-2 text-[12px] text-[var(--danger)]"
            >
              {formError}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            {/* Never disabled on validation — pressing it explains what is missing. */}
            <Button type="submit" variant="primary" disabled={create.isPending}>
              {create.isPending ? <Loader2 className="animate-spin" /> : <FolderPlus />}
              Create project
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
